/**
 * Local history of copied formula payloads (last N entries).
 *
 * Persisted to localStorage so copying in one drawer and pasting in another
 * works even after the page reloads.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  FormulaClipboardHistoryEntry,
  FormulaClipboardPayload,
} from './formula-clipboard.types';

const STORAGE_KEY = 'traffic-pulse:formula-clipboard:history';
const MAX_ENTRIES = 10;

function safeStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readHistory(): FormulaClipboardHistoryEntry[] {
  const store = safeStorage();
  if (!store) return [];
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isHistoryEntry).slice(0, MAX_ENTRIES);
  } catch {
    return [];
  }
}

export function writeHistory(entries: FormulaClipboardHistoryEntry[]): void {
  const store = safeStorage();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    // Quota exceeded or storage disabled — fail silently.
  }
}

export function pushHistory(payload: FormulaClipboardPayload): FormulaClipboardHistoryEntry[] {
  const entry: FormulaClipboardHistoryEntry = {
    ...payload,
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
  };
  const current = readHistory();
  const next = [entry, ...current.filter((e) => !samePayload(e, entry))].slice(0, MAX_ENTRIES);
  writeHistory(next);
  return next;
}

export function clearHistory(): void {
  const store = safeStorage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function samePayload(a: FormulaClipboardPayload, b: FormulaClipboardPayload): boolean {
  if (a.type !== b.type) return false;
  return JSON.stringify(a.data) === JSON.stringify(b.data);
}

function isHistoryEntry(value: unknown): value is FormulaClipboardHistoryEntry {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === 'string' && typeof v.label === 'string' && typeof v.copiedAt === 'string';
}
